/**
 * useFavorites Hook
 *
 * TanStack Query hook for managing user favorites with optimistic updates
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@teletienda/shared-auth-ui';
import {
  favoritesAPI,
  type FavoriteItem,
  type FavoritesListResponse,
  type FavoriteEntityType,
  type AddFavoriteRequest,
} from '@/lib/api/favorites';

// Query key prefix for favorites
const FAVORITES_KEY = ['favorites'] as const;

/**
 * Options for useFavorites hook
 */
export interface UseFavoritesOptions {
  /** Filter by entity type */
  type?: FavoriteEntityType;
  /** Filter by merchant GUID */
  merchantGuid?: string;
  /** Page number (1-indexed) */
  page?: number;
  /** Items per page */
  limit?: number;
  /** Enable/disable the query */
  enabled?: boolean;
}

/**
 * Return type for useFavorites hook
 */
export interface UseFavoritesReturn {
  // Data
  favorites: FavoriteItem[];
  pagination: FavoritesListResponse['pagination'] | undefined;

  // Query state
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: Error | null;

  // Mutations
  addFavorite: (data: AddFavoriteRequest) => Promise<FavoriteItem>;
  removeFavorite: (params: {
    entityType: FavoriteEntityType;
    entityId: string;
  }) => Promise<{ deleted: boolean }>;
  toggle: (
    entityType: FavoriteEntityType,
    entityId: string,
    metadata?: Record<string, unknown>
  ) => Promise<void>;
  isAdding: boolean;
  isRemoving: boolean;

  // Helpers
  isFavorited: (entityType: FavoriteEntityType, entityId: string) => boolean;
  isAuthenticated: boolean;

  // Refetch
  refetch: () => Promise<unknown>;
}

/**
 * Hook for managing user favorites
 *
 * Provides list, add, remove, toggle, and check operations
 * with optimistic updates for instant UI feedback
 *
 * @example
 * ```tsx
 * function FavoritesList() {
 *   const { favorites, isLoading, pagination } = useFavorites();
 *
 *   if (isLoading) return <Loader />;
 *
 *   return (
 *     <div>
 *       {favorites.map((fav) => (
 *         <div key={fav.favorite_id}>
 *           {fav.entity_type}: {fav.entity_id}
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // With filters
 * const { favorites } = useFavorites({
 *   type: 'product',
 *   merchantGuid: '0169c682-bdce-4b69-8876-10aaf0d76d08',
 *   page: 1,
 *   limit: 10,
 * });
 * ```
 *
 * @example
 * ```tsx
 * // Toggle favorite
 * function ProductCard({ product }) {
 *   const { toggle, isFavorited, isAdding, isRemoving } = useFavorites();
 *
 *   return (
 *     <Card>
 *       <IconButton
 *         onClick={() => toggle('product', product.id, { merchant_guid: product.merchantGuid })}
 *         loading={isAdding || isRemoving}
 *       >
 *         <HeartIcon filled={isFavorited('product', product.id)} />
 *       </IconButton>
 *     </Card>
 *   );
 * }
 * ```
 */
export function useFavorites(options: UseFavoritesOptions = {}): UseFavoritesReturn {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  const { type, merchantGuid, page = 1, limit = 20, enabled = true } = options;

  // Build query key with filters
  const queryKey = [...FAVORITES_KEY, { type, merchantGuid, page, limit }];

  // Query for favorites list
  const favoritesQuery = useQuery({
    queryKey,
    queryFn: () =>
      favoritesAPI.list({
        type,
        merchant_guid: merchantGuid,
        page,
        limit,
      }),
    enabled: enabled && isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Add favorite mutation with optimistic update
  const addMutation = useMutation({
    mutationFn: (data: AddFavoriteRequest) => favoritesAPI.add(data),
    onMutate: async (newFavorite) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: FAVORITES_KEY });

      // Snapshot previous value for all favorites queries
      const previousQueries = queryClient.getQueriesData<FavoritesListResponse>({
        queryKey: FAVORITES_KEY,
      });

      // Optimistically update all favorites queries
      queryClient.setQueriesData<FavoritesListResponse>(
        { queryKey: FAVORITES_KEY },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: [
              {
                favorite_id: `temp-${Date.now()}`,
                entity_type: newFavorite.entity_type,
                entity_id: newFavorite.entity_id,
                metadata: newFavorite.metadata,
                created_at: new Date().toISOString(),
              },
              ...old.items,
            ],
            pagination: {
              ...old.pagination,
              total: old.pagination.total + 1,
            },
          };
        }
      );

      // Also invalidate check queries
      queryClient.invalidateQueries({ queryKey: ['favorites-check'] });

      return { previousQueries };
    },
    onError: (_err, _newFavorite, context) => {
      // Rollback on error
      if (context?.previousQueries) {
        for (const [key, data] of context.previousQueries) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      // Refetch to sync with server
      queryClient.invalidateQueries({ queryKey: FAVORITES_KEY });
      queryClient.invalidateQueries({ queryKey: ['favorites-check'] });
    },
  });

  // Remove favorite mutation with optimistic update
  const removeMutation = useMutation({
    mutationFn: ({
      entityType,
      entityId,
    }: {
      entityType: FavoriteEntityType;
      entityId: string;
    }) => favoritesAPI.remove(entityType, entityId),
    onMutate: async ({ entityType, entityId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: FAVORITES_KEY });

      // Snapshot previous value
      const previousQueries = queryClient.getQueriesData<FavoritesListResponse>({
        queryKey: FAVORITES_KEY,
      });

      // Optimistically remove from all favorites queries
      queryClient.setQueriesData<FavoritesListResponse>(
        { queryKey: FAVORITES_KEY },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.filter(
              (f) =>
                !(f.entity_type === entityType && f.entity_id === entityId)
            ),
            pagination: {
              ...old.pagination,
              total: Math.max(0, old.pagination.total - 1),
            },
          };
        }
      );

      // Also invalidate check queries
      queryClient.invalidateQueries({ queryKey: ['favorites-check'] });

      return { previousQueries };
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousQueries) {
        for (const [key, data] of context.previousQueries) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      // Refetch to sync with server
      queryClient.invalidateQueries({ queryKey: FAVORITES_KEY });
      queryClient.invalidateQueries({ queryKey: ['favorites-check'] });
    },
  });

  /**
   * Check if item is favorited (from cache)
   */
  const isFavorited = (entityType: FavoriteEntityType, entityId: string): boolean => {
    const data = favoritesQuery.data;
    if (!data) return false;
    return data.items.some(
      (f) => f.entity_type === entityType && f.entity_id === entityId
    );
  };

  /**
   * Toggle favorite status
   */
  const toggle = async (
    entityType: FavoriteEntityType,
    entityId: string,
    metadata?: Record<string, unknown>
  ): Promise<void> => {
    if (isFavorited(entityType, entityId)) {
      await removeMutation.mutateAsync({ entityType, entityId });
    } else {
      await addMutation.mutateAsync({
        entity_type: entityType,
        entity_id: entityId,
        metadata,
      });
    }
  };

  return {
    // Data
    favorites: favoritesQuery.data?.items ?? [],
    pagination: favoritesQuery.data?.pagination,

    // Query state
    isLoading: favoritesQuery.isLoading,
    isFetching: favoritesQuery.isFetching,
    isError: favoritesQuery.isError,
    error: favoritesQuery.error,

    // Mutations
    addFavorite: addMutation.mutateAsync,
    removeFavorite: removeMutation.mutateAsync,
    toggle,
    isAdding: addMutation.isPending,
    isRemoving: removeMutation.isPending,

    // Helpers
    isFavorited,
    isAuthenticated,

    // Refetch
    refetch: favoritesQuery.refetch,
  };
}

/**
 * Options for useFavoritesCheck hook
 */
export interface UseFavoritesCheckOptions {
  /** Enable/disable the query */
  enabled?: boolean;
}

/**
 * Hook for batch checking favorites (for product lists)
 *
 * Efficiently checks multiple items at once to avoid N+1 queries
 *
 * @example
 * ```tsx
 * function ProductGrid({ products }) {
 *   const productIds = products.map((p) => p.id);
 *   const { data: favoritesMap } = useFavoritesCheck('product', productIds);
 *
 *   return (
 *     <Grid>
 *       {products.map((product) => (
 *         <ProductCard
 *           key={product.id}
 *           product={product}
 *           isFavorited={favoritesMap?.[product.id] ?? false}
 *         />
 *       ))}
 *     </Grid>
 *   );
 * }
 * ```
 */
export function useFavoritesCheck(
  entityType: FavoriteEntityType,
  entityIds: string[],
  options: UseFavoritesCheckOptions = {}
) {
  const { isAuthenticated } = useAuth();
  const { enabled = true } = options;

  return useQuery({
    queryKey: ['favorites-check', entityType, entityIds],
    queryFn: () =>
      favoritesAPI.check({
        entity_type: entityType,
        entity_ids: entityIds,
      }),
    enabled: enabled && isAuthenticated && entityIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
